import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send, Sprout } from 'lucide-react';
import { FEEDBACK_CATEGORIES, FEEDBACK_COPY, FEEDBACK_MAX_LENGTH } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';

/** 불편·제안 보내기 (v1.18) — 이용자의 목소리가 운영진 건의함으로 모인다 */
export function FeedbackPage() {
  const [category, setCategory] = useState<string>(FEEDBACK_CATEGORIES[0].code);
  const [content, setContent] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => apiData('/feedback', { method: 'POST', body: { category, content } }),
    onSuccess: () => {
      setSent(true);
      setContent('');
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '보내지 못했어요. 잠시 후 다시 시도해 주세요.'),
  });

  if (sent) {
    return (
      <Screen>
        <AppBar title={FEEDBACK_COPY.title} back />
        <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-primary-soft text-primary-strong">
            <Sprout size={26} />
          </span>
          <p className="text-lg font-bold">{FEEDBACK_COPY.doneTitle}</p>
          <p className="text-base leading-relaxed text-ink-sub">{FEEDBACK_COPY.doneBody}</p>
          <Button variant="outline" className="mt-2" onClick={() => setSent(false)}>
            {FEEDBACK_COPY.another}
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <AppBar title={FEEDBACK_COPY.title} back />

      <div className="flex flex-col gap-4 p-4">
        <p className="text-base leading-relaxed text-ink-sub">{FEEDBACK_COPY.intro}</p>

        <div className="flex flex-wrap gap-2">
          {FEEDBACK_CATEGORIES.map((item) => (
            <Chip
              key={item.code}
              tone={category === item.code ? 'primary' : 'neutral'}
              selected={category === item.code}
              onClick={() => setCategory(item.code)}
            >
              {item.label}
            </Chip>
          ))}
        </div>

        <Card>
          <Textarea
            rows={6}
            maxLength={FEEDBACK_MAX_LENGTH}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={FEEDBACK_COPY.placeholder}
          />
          <p className="mt-1.5 text-right text-sm text-ink-sub tnum">
            {content.length}/{FEEDBACK_MAX_LENGTH}
          </p>
        </Card>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <Button
          block
          icon={<Send size={16} />}
          loading={submit.isPending}
          disabled={!content.trim()}
          onClick={() => submit.mutate()}
        >
          {FEEDBACK_COPY.submit}
        </Button>
      </div>
    </Screen>
  );
}
